import { compareIso } from './dates.js'
import type { IsoDate } from './dates.js'
import { applyRate } from './money.js'
import type { Minor } from './money.js'
import type { FxRate } from './types.js'

/**
 * Narzut za przewalutowanie.
 *
 * Notowanie NBP to kurs referencyjny — bank czy karta nigdy nie rozliczy po
 * nim rachunku. Realny koszt firmy jest wyższy o spread, więc każdą kwotę
 * w walucie innej niż złoty liczymy z tym narzutem. Rachunki w złotych
 * nie przechodzą przez przewalutowanie, więc ich nie dotyczy.
 */
export const FX_MARKUP = 1.055

export interface Conversion {
  amountPlnMinor: Minor
  /** Kurs faktycznie użyty do wyliczenia kwoty: notowanie NBP razy narzut. */
  rate: number
  /** Samo notowanie NBP, bez narzutu — do porównań i kontroli. */
  rateNbp: number
  rateDate: IsoDate
}

/**
 * Znajduje kurs obowiązujący na dany dzień: notowanie z tego dnia albo
 * ostatnie wcześniejsze (weekendy i święta nie mają notowań NBP).
 */
export function findRate(rates: readonly FxRate[], currency: string, on: IsoDate): FxRate | null {
  let best: FxRate | null = null
  for (const rate of rates) {
    if (rate.currency !== currency) continue
    if (compareIso(rate.date, on) > 0) continue
    if (best === null || compareIso(rate.date, best.date) > 0) best = rate
  }
  return best
}

export function convertToPln(
  amount: Minor,
  currency: string,
  on: IsoDate,
  rates: readonly FxRate[],
): Conversion | null {
  if (currency === 'PLN') {
    return { amountPlnMinor: amount, rate: 1, rateNbp: 1, rateDate: on }
  }
  const found = findRate(rates, currency, on)
  if (found === null) return null
  // Mnożymy kurs, a nie gotową kwotę — jedno zaokrąglenie na końcu zamiast
  // dwóch, więc grosze nie uciekają przy sumowaniu wielu pozycji.
  const zNarzutem = found.rate * FX_MARKUP
  return {
    amountPlnMinor: applyRate(amount, zNarzutem),
    rate: zNarzutem,
    rateNbp: found.rate,
    rateDate: found.date,
  }
}
